import React from 'react';
import { useRouteMatch, Switch, Redirect, useHistory } from 'react-router-dom';
import AuthorizedRoute from '../../../permission/AuthorizedRoute';
import { BulkRequestList } from '../../../components/bulkRequest';
import { PAGE_NAMES, RESOURCES, SETTINGS_V2_ACTION } from '../../../settingsV2Constant';
import usePermission from '../../../permission/usePermission';
import { ENTITIES } from './constants';
import { ACTION_IDS } from '../../../components/bulkRequest/actionIds';
import CreateRequest from './submodules/createRequest';
import useService from './utils/useService';
import { columnConfigs, statusColumnConfigs } from './listConfig';
import { DEFAULT_CONFIG } from './helper';
import { VIEW_STATUS_ACTION_NAME, CREATE_ACTION_NAME } from '../../../components/bulkRequest/constants';

const makeActions = (hasPermission: ReturnType<typeof usePermission>['hasPermission']) => {
	const withPerm = (actionName: string) =>
		hasPermission({
			configPageName: PAGE_NAMES.settingsV2,
			configResourceName: RESOURCES.bulkUserCsv,
			configActionName: actionName
		});

	return {
		bulkActions: [],
		rowActions: [
			{
				name: VIEW_STATUS_ACTION_NAME,
				id: ACTION_IDS.VIEW_STATUS,
				isEnabled: () => withPerm(SETTINGS_V2_ACTION.checkBulkUserCsvRequestStatus)
			}
		],
		addAction: {
			name: CREATE_ACTION_NAME,
			id: ACTION_IDS.ADD,
			isEnabled: () => withPerm(SETTINGS_V2_ACTION.createBulkUserCsvRequest)
		}
	};
};

const usePageActions = () => {
	const history = useHistory();

	const getActionHandler = (action: any) => {
		switch (action.id) {
			case ACTION_IDS.ADD:
				return () => {
					const basePath = history.location.pathname.replace(/\/list$/, '');
					history.push(`${basePath}/create`);
				};
			case ACTION_IDS.VIEW_STATUS:
				return () => {};
			default:
				return () => {};
		}
	};

	const { hasPermission } = usePermission();
	const getActionList = () => makeActions(hasPermission);
	return { getActionHandler, getActionList };
};

const BulkUserCsvDashboard: React.FC = () => {
	const { path } = useRouteMatch();
	const service = useService();
	const { getActionHandler, getActionList } = usePageActions();
	const actions = getActionList();

	return (
		<Switch>
			<AuthorizedRoute
				configPageName={PAGE_NAMES.settingsV2}
				configResourceName={RESOURCES.bulkUserCsv}
				configActionName={SETTINGS_V2_ACTION.listBulkUserCsvRequests}
				path={`${path}/list`}
				component={() => (
					<BulkRequestList
						defaults={DEFAULT_CONFIG}
						fetchData={service.listRequests}
						statusFetchData={service.checkStatus}
						columnConfigs={columnConfigs}
						statusColumnConfigs={statusColumnConfigs}
						entity={ENTITIES.BULK_USER_CSV}
						statusEntity={ENTITIES.BULK_USER_CSV_STATUS}
						forceUpdate={service.forceUpdate}
						turnForceUpdateOff={service.turnForceUpdateOff}
						actions={actions}
						getActionHandler={getActionHandler}
					/>
				)}
				exact
			/>
			<AuthorizedRoute
				configPageName={PAGE_NAMES.settingsV2}
				configResourceName={RESOURCES.bulkUserCsv}
				configActionName={SETTINGS_V2_ACTION.createBulkUserCsvRequest}
				path={`${path}/create`}
				component={CreateRequest}
				exact
			/>
			<Redirect to={`${path}/list`} />
		</Switch>
	);
};

export default BulkUserCsvDashboard;
